import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Select,
  InputNumber,
  message,
  Spin,
  Space,
  Row,
  Col,
  Radio,
} from 'antd';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getArtworkStyleDetail,
  getAllArtworkStyleTypes,
  addArtworkStyle,
  updateArtworkStyle,
  ArtworkAiLiblibStyleDictDTO,
  artworkLiblibTypeStyle,
} from '@/api/liblib';
import UploadImage from '@/components/UploadImage';
import { PageLink } from '@/config/pageConfig';
import YesNoRadio from '@/pages/text-to-image/style/components/YesNoRadio';

const { Option } = Select;

const LiblibDetail: React.FC = () => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [styleTypeOptions, setStyleTypeOptions] = useState<
    { styleType: string; styleTypeJa?: string }[]
  >([]);
  const [imageUrl, setImageUrl] = useState<string | undefined>();
  const [disableStyleTypeInputs, setDisableStyleTypeInputs] = useState(false);

  useEffect(() => {
    fetchStyleTypeOptions();
    if (isEdit) {
      fetchDetail(Number(id));
    }
  }, [id]);

  const fetchStyleTypeOptions = async () => {
    // const { data } = await getAllArtworkStyleTypes();
    const {data } = await artworkLiblibTypeStyle();
    setStyleTypeOptions(
      data.data
        .filter((item) => item.styleTypeId)
    );
  };

  const fetchDetail = async (styleId: number) => {
    setLoading(true);
    try {
      const { data } = await getArtworkStyleDetail(styleId);
      const detail = data.data;
      form.setFieldsValue({
        ...detail,
        isVipStyle: detail.isVipStyle ?? 0,
        // freeUse: detail.freeUse ?? 0,
      });
      if (detail.styleImage) {
        setImageUrl(detail.styleImage);
        form.setFieldValue('styleImage', detail.styleImage);
      }

      // // 如果是已存在的风格类型，自动联动 styleTypeJa 并禁用输入框
      // const matched = styleTypeOptions.find(
      //   (item) => item.styleType === detail.styleType
      // );
      // if (matched && matched.styleTypeJa) {
      //   form.setFieldValue('styleTypeJa', matched.styleTypeJa);
      //   setDisableStyleTypeInputs(true);
      // }
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    const values = await form.validateFields();
    const payload: ArtworkAiLiblibStyleDictDTO = {
      ...values,
      styleImage: imageUrl,
      isVipStyle: values.isVipStyle ?? 0,
      // freeUse: values.isVipStyle === 1 ? (values.freeUse ?? 0) : 0,
    };
    try {
      const res = isEdit
        ? await updateArtworkStyle({ ...payload, styleId: Number(id) })
        : await addArtworkStyle(payload);

      if (res.data.success) {
        message.success(`${isEdit ? '编辑' : '新增'}成功`);
        // 获取当前页码，如果有则携带参数跳转
        const currentPage = searchParams.get('page');
        const navigateUrl = currentPage
          ? `${PageLink.Liblib}?page=${currentPage}`
          : PageLink.Liblib;
        navigate(navigateUrl);
      } else {
        message.error(res.data.message || '操作失败');
      }
    } catch {
      message.error('请求失败，请稍后重试');
    }
  };

  return (
    <Spin spinning={loading}>
      <Form
        form={form}
        labelCol={{ flex: '0 0 140px' }}
        wrapperCol={{ flex: 1 }}
        labelAlign="left"
        colon={false}
        layout="horizontal"
        style={{ maxWidth: 900, margin: '0 auto' }}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="styleTypeId"
              label="风格类型"
              rules={[{ required: true, message: '请选择风格类型' }]}
            >
              <Select
                allowClear
                placeholder="选择已有风格"
                // onChange={(val) => {
                //   if (val) {
                //     const matched = styleTypeOptions.find(
                //       (item) => item.styleType === val
                //     );
                //     if (matched?.styleTypeJa) {
                //       form.setFieldValue('styleTypeJa', matched.styleTypeJa);
                //       setDisableStyleTypeInputs(true);
                //     } else {
                //       form.setFieldValue('styleTypeJa', undefined);
                //       setDisableStyleTypeInputs(false);
                //     }
                //   } else {
                //     setDisableStyleTypeInputs(false);
                //     form.setFieldsValue({
                //       styleType: undefined,
                //       styleTypeJa: undefined,
                //     });
                //   }
                // }}
              >
                {styleTypeOptions.map((item) => (
                  <Option key={item.styleType} value={item.styleTypeId}>
                    {item.styleType}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Form.Item
            name="styleNameSort"
            label="主题排序"
            rules={[{ required: true, message: '请输入主题排序' }]}
          >
            <InputNumber style={{ width: '100%' }} placeholder="数字越大越靠前" />
          </Form.Item>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="styleNameJa"
              label="主题名称（日）"
              rules={[{ required: true, message: '请输入日文主题名' }]}
            >
              <Input placeholder="（日）输入主题名称" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="styleName"
              label="主题名称（英）"
              rules={[{ required: true, message: '请输入英文主题名' }]}
            >
              <Input placeholder="（英）输入主题名称" />
            </Form.Item>
          </Col>
        </Row>
        
        <Form.Item
          name="styleImage"
          label="风格图片"
          rules={[{ required: true, message: '请上传风格图' }]}
        >
          <UploadImage
            width={800}
            height={400}
            maxSize={10}
            value={imageUrl}
            showSizeHint={false}
            acceptWebp
            onChange={(url: string) => {
              setImageUrl(url);
              form.setFieldValue('styleImage', url);
            }}
          />
        </Form.Item>

        <Form.Item
          name="triggerWord"
          label="触发词"
          tooltip="LoRA 的触发词，填模型官网标注的值。不挂 LoRA 的风格（只用底模）没有触发词，留空即可——例如 10241 澄明アニメ。"
          extra="留空则不注入。不挂 LoRA 的风格没有触发词"
        >
          <Input placeholder="留空则不注入" />
        </Form.Item>

        <Form.Item
          name="positivePrompt"
          label="正向提示词（英）"
          tooltip="只写画风/质量词。不要写 detailed background、daytime 这类会顶掉用户指定的词——实测用户写的 white_background 就是被 detailed background 顶掉的。用户自己写满 300 字以上时，后端会自动跳过注入。"
          extra="留空则不注入，用户提示词原样送出"
        >
          <Input.TextArea placeholder="留空则不注入" rows={3} />
        </Form.Item>

        <Form.Item
          name="positivePromptJa"
          label="正向提示词（日）"
          extra="仅后台展示用，实际送给 liblib 的是英文那栏"
        >
          <Input.TextArea placeholder="留空则不注入" rows={3} />
        </Form.Item>

        <Form.Item
          name="negativePrompt"
          label="负向提示词"
          tooltip="建议填模型官网推荐的负向词，并务必带上 nsfw / nude 等防护词——Illustrious 系模型在短提示词下会默认脱衣，实测无害输入 4/4 出裸图。"
          extra="纯英文、2000 字以内。留空用后端默认值"
          rules={[{ max: 2000, message: '负向提示词不能超过 2000 字' }]}
        >
          <Input.TextArea placeholder="请输入负向提示词（纯英文）" rows={3} />
        </Form.Item>

        <Form.Item
          name="fallbackStyleId"
          label="兜底风格 ID"
          tooltip="🔴 只能填同源风格。例：10082 幻月の再光 → 110 幻月の再光 プロ（两者用同一个 幻月重光 LoRA）。填无关风格会让用户拿到完全不同画风的图。"
          extra="NSFW / 超 1800 字 / 翻译失败时改用这个 SuppX 风格。留空则失败退币"
          rules={[{ type: 'number', min: 1, message: '请输入有效的 SuppX style_id' }]}
        >
          <InputNumber placeholder="留空则不兜底" style={{ width: '100%' }} precision={0} />
        </Form.Item>

        <Form.Item
          name="cfg"
          label="提示词引导系数"
          tooltip="F.1(Flux) 系模型官网多推荐 3.5，XL / Illustrious 系多为 7。以底模官网页的推荐值为准，不要用 LoRA 页的值。"
          extra="1.0~15.0，可填小数"
          rules={[{ type: 'number', min: 1, max: 15, message: '引导系数需在 1.0~15.0 之间' }]}
        >
          <InputNumber
            style={{ width: '100%' }}
            step={0.5}
            placeholder="请输入提示词引导系数"
          />
        </Form.Item>

        <Form.Item
          name="sampler"
          label="采样器"
          extra="留空使用后端默认值。官方推荐度见 API 文档 7.5，多数模型页推荐 Euler a。"
        >
          <Select allowClear placeholder="留空则使用后端默认值" options={[
            { value: 0, label: '0 - Euler a ⭐⭐⭐⭐⭐' },
            { value: 1, label: '1 - Euler ⭐⭐⭐' },
            { value: 7, label: '7 - DPM++ 2M ⭐⭐⭐' },
            { value: 10, label: '10 - DPM++ Adaptive ⭐⭐⭐⭐' },
            { value: 15, label: '15 - DPM++ 2M Karras ⭐⭐⭐⭐⭐' },
            { value: 16, label: '16 - DPM++ SDE Karras ⭐⭐⭐⭐⭐' },
            { value: 20, label: '20 - DPM++ 2M SDE Karras ⭐⭐⭐⭐⭐' },
            { value: 27, label: '27 - DPM++ 3M SDE Karras ⭐⭐⭐⭐' },
          ]} />
        </Form.Item>

        <Form.Item
          name="steps"
          label="采样步数"
          extra="1~60。留空使用后端默认值。步数越高越慢，注意后端有 90 秒轮询上限。"
          rules={[{ type: 'number', min: 1, max: 60, message: '采样步数需在 1~60 之间' }]}
        >
          <InputNumber style={{ width: '100%' }} placeholder="留空则使用后端默认值" />
        </Form.Item>

        <Form.Item
          name="upscaler"
          label="高清放大算法"
          tooltip="🔴 高清修复要同时满足两个条件才生效：「像素倍率」> 1 且「高清修复步数」已填。只填这一栏不起作用。"
          extra="动漫风格选 Anime6B"
        >
          <Select allowClear placeholder="留空则使用后端默认值" options={[
            { value: 6, label: '6 - Lanczos（高细节图像）' },
            { value: 10, label: '10 - R-ESRGAN_4x+（写实/照片向，动漫略逊）' },
            { value: 11, label: '11 - R-ESRGAN_4x+ Anime6B（动漫专用，推荐）' },
            { value: 14, label: '14 - SwinIR_4x（复杂场景）' },
          ]} />
        </Form.Item>

        <Form.Item
          name="hiresSteps"
          label="高清修复步数"
          tooltip="🔴 开启前务必实测耗时。后端轮询上限 90 秒，两段生成很容易超时导致生图失败并退币——10082 和 10224 都踩过这个坑。"
          extra="🔴 高清修复总开关。留空 = 不做高清修复，另外两栏随之失效。1~30"
          rules={[{ type: 'number', min: 1, max: 30, message: '高清修复步数需在 1~30 之间' }]}
        >
          <InputNumber style={{ width: '100%' }} placeholder="留空 = 不做高清修复" />
        </Form.Item>

        <Form.Item
          name="hiresDenoisingStrength"
          label="高清修复去噪强度"
          tooltip="0.7 以上会明显改变构图，社区共识是 0.35~0.5。仅在高清修复开启时生效。"
          extra="0~1。数值越大重绘越多。留空用后端默认值"
          rules={[{ type: 'number', min: 0, max: 1, message: '去噪强度需在 0~1 之间' }]}
        >
          <InputNumber style={{ width: '100%' }} step={0.1} placeholder="留空则使用后端默认值" />
        </Form.Item>

        <Form.Item
          name="vaeId"
          label="Style versionUuid"
        >
          <Input placeholder="填入liblib的versionUuid:权重" />
        </Form.Item>

        <Form.Item
          name="versionUuid"
          label="Model versionUuid"
          rules={[{ required: true, message: '请输入Model versionUuid' }]}
        >
          <Input placeholder="填入liblib的底模ID" />
        </Form.Item>

        <Form.Item
          name="model"
          label="model"
        >
          <Input placeholder="请输入model" />
        </Form.Item>

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="isRecommend"
              label="是否推荐到首页"
              rules={[{ required: true, message: '请选择是否推荐' }]}
            >
              <Select
                placeholder="请选择"
                onChange={() => {
                  const is = form.getFieldValue('isRecommend');
                  if (is === 1) {
                    form.validateFields(['recommendSort']);
                  }
                }}
              >
                <Option value={1}>是</Option>
                <Option value={0}>否</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="recommendSort"
              label="首页推荐排序"
              rules={[
                {
                  validator: (_, value) => {
                    const isRecommend = form.getFieldValue('isRecommend');
                    if (
                      isRecommend === 1 &&
                      (value === undefined || value === null)
                    ) {
                      return Promise.reject('推荐时必须填写推荐排序');
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="数字越大越靠前"
              />
            </Form.Item>
          </Col>

        </Row>
        <Form.Item
          name="limitTime"
          label="期间限定"
          rules={[{ required: true, message: '请选择' }]}
        >
          <Radio.Group
            options={[
              { value: 1, label: "是" },
              { value: 0, label: "否" },
            ]}
          >
          </Radio.Group>

        </Form.Item>
        <Form.Item
          name="status"
          label="是否启用"
          rules={[{ required: true, message: '请选择' }]}
        >
          <Radio.Group
            options={[
              { value: 1, label: '是' },
              { value: 0, label: '否' },
            ]}
          ></Radio.Group>
        </Form.Item>

        <Form.Item
          name="defaultSelected"
          label="默认选中"
          rules={[{ required: true, message: '请选择' }]}
        >
          <Radio.Group
            options={[
              { value: 1, label: '是' },
              { value: 0, label: '否' },
            ]}
          ></Radio.Group>
        </Form.Item>

        <Form.Item
          name="isVipStyle"
          label="是否会员风格"
          rules={[{ required: true, message: '请选择是否会员风格' }]}
          initialValue={0}
        >
          <YesNoRadio />
        </Form.Item>

        {/* <Form.Item noStyle shouldUpdate={(prev, cur) => prev.isVipStyle !== cur.isVipStyle}>
          {({ getFieldValue }) =>
            getFieldValue('isVipStyle') === 1 ? (
              <Form.Item
                name="freeUse"
                label="vip风格免费试用"
                rules={[{ required: true, message: '请选择' }]}
                initialValue={0}
              >
                <Radio.Group
                  options={[
                    { value: 0, label: '不免费' },
                    { value: 1, label: '免费' },
                  ]}
                />
              </Form.Item>
            ) : null
          }
        </Form.Item> */}

        <Form.Item
          name="minTierLevel"
          label="最低可用等级"
          tooltip="设置该风格所需的最低会员等级（前包含后）"
        >
          <Select placeholder="请选择最低可用等级" allowClear>
            <Option value={-1}>Free（所有用户可用）</Option>
            <Option value={2}>Lite（Lite/Pro/Premium 可用）</Option>
            <Option value={0}>Pro（Pro/Premium 可用）</Option>
            <Option value={1}>Premium（仅 Premium 可用）</Option>
          </Select>
        </Form.Item>

        <div>消耗数量</div>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="gcoins"
              label="金币"
              rules={[{ required: true, message: '请选择充值金币' }]}
            >
              <Input placeholder="请输入充值金币" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="pcoins"
              label="活动金币"
              rules={[{ required: true, message: '请选择活动金币' }]}
            >
              <Input placeholder="请输入活动金币" />
            </Form.Item>
          </Col>
        </Row>
        
        <Form.Item
          name="pixel"
          label="像素"
          rules={[{ required: true, message: '请输入' }]}
        >
          <InputNumber min={0} precision={1} />
        </Form.Item>

        {/* E15 高级功能配置 */}
        <Form.Item name="support4k" label="支持4K" initialValue={0}>
          <Radio.Group>
            <Radio value={0}>否</Radio>
            <Radio value={1}>是</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item name="supportInpaint" label="支持局部重绘" initialValue={0}>
          <Radio.Group>
            <Radio value={0}>否</Radio>
            <Radio value={1}>是</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item wrapperCol={{ offset: 5 }}>
          <Space>
            <Button type="primary" onClick={onSubmit}>
              提交
            </Button>
            <Button onClick={() => navigate(-1)}>取消</Button>
          </Space>
        </Form.Item>
      </Form>
    </Spin>
  );
};

export default LiblibDetail;